<template>
    <main
        class="layout__main--narrow search"
        :class="{
            'search--narrow': narrowSearch,
            'search--patients': activeSite?.group === 'patients'
        }"
    >
        <h1>
            <template v-if="activeSite?.group === 'patients'">
                {{ t('search-title-alternate') }}
            </template>
            <template v-else>
                {{ t('search-title') }}
            </template>
        </h1>
        <BaseSection>
            <TheSearchForm />

            <div class="search__meta">
                <template v-if="searched && !pending">
                    <span>{{ totalEntries || t('search-none') }} {{ t(totalEntries !== 1 ? 'search-results' : 'search-result') }} {{ t('search-for') }} &apos;{{ searched }}&apos;</span>
                    <BaseButton
                        v-if="searched && !pending"
                        class="dn-button--link"
                        @click="clearSearch"
                    >
                        {{ t('search-clear') }}
                    </BaseButton>
                </template>
            </div>
        </BaseSection>

        <BaseSection>
            <template v-if="searched && !pending">
                <BaseSearchCard
                    v-for="item in entries"
                    :key="item.id"
                    :item="item"
                />
            </template>
            <template v-else-if="searched">
                <BaseSearchCard
                    v-for="i in [1,2,3,4]"
                    :key="i"
                >
                    <ContentPlaceholders>
                        <ContentPlaceholdersHeading />
                        <ContentPlaceholdersText :lines="2" />
                    </ContentPlaceholders>
                </BaseSearchCard>
            </template>
        </BaseSection>

        <BaseSection>
            <BasePaginator
                v-if="searched && !pending"
                :current-page="currentPage"
                :total-pages="totalPages"
            />
        </BaseSection>
    </main>
</template>

<script setup lang="ts">
import SearchResultsQuery from '~/graphql/queries/generic/globalSearchIndex.graphql';

const t = useTranslation('site');
const SEARCH_POSTS_PER_PAGE = ref(9);
const searched = ref<string>('');

const {activeSite, variables: baseVariables} = useMultisite();

useHead({
    title: `Search - ${toValue(activeSite)?.name ?? 'EAU'}`,
    // description: 'Search through all the content on Uroweb.org'
});

const route = useRoute();
const router = useRouter();

const searchParam = computed(() => {
    return route.query.q;
});

const currentPage = computed(() => {
    return parseInt(route.query.page as string ?? '1', 10) || 1;
});
const decodedSearch = computed(() => {
    return decodeURI(searchParam.value as string ?? '') || '';
});

const offset = computed(() => {
    return (SEARCH_POSTS_PER_PAGE.value * currentPage.value) - SEARCH_POSTS_PER_PAGE.value;
});

const variables = reactive({
    ...toValue(baseVariables),
    limit: SEARCH_POSTS_PER_PAGE.value,
    offset,
    search: decodedSearch
});

const { data, pending } = await useLazyAsyncQuery({
    query: SearchResultsQuery,
    variables
});

const {entries, totalEntries} = useFilteredSearchResults(data);

const totalPages = computed(() => {
    if (!totalEntries.value) {
        return 1;
    }

    return Math.ceil(totalEntries.value / SEARCH_POSTS_PER_PAGE.value);
});

watch(searchParam, () => {
    searched.value = searchParam.value as string;
}, { immediate: true });

watch(currentPage, () => {
    window.scrollTo(0, 0);
});

const narrowSearch = computed(() => {
    const siteGroupsWithNarrowSearch = ['patients'];

    return siteGroupsWithNarrowSearch.includes(toValue(activeSite)?.group ?? false);
});

function clearSearch() {
    searched.value = '';
    router.push({ path: '/search', query: { q: '' } });
}
</script>

<style lang="less">
.search__meta {
    .typography-text();
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-top: 1.375rem;
}

.search--narrow {
    --grid-maxWidth-content: 37.75rem;
    max-width: var(--grid-maxWidth-content);

    @media @q-md-min {
        --gap: 0 !important; /* stylelint-disable-line declaration-no-important */
    }
}

.search--patients {
    .dn-button--link {
        color: var(--color-alt-dark);
        font-weight: 700;
        font-family: Spartan, sans-serif;
        letter-spacing: -1px;
        line-height: .8;
    }

    .pill {
        background: var(--color-alt-light);
        color: var(--color-alt-dark);
        font-size: .875rem;
    }
}
</style>
